import { useState, useRef, useEffect } from 'react';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Mail, MapPin, Building2, Tag, Send, Clock,
  MessageSquare, CheckCircle2, Globe, Briefcase, Phone,
  AlertCircle, Loader2, Trash2, Users, DollarSign, Eye,
} from 'lucide-react';
import { get, post, del } from '@/lib/apiClient';
import { API_URL } from '@/_config';
import { useRegisterBreadcrumbTitle } from '@/context/BreadcrumbContext';
import ConfirmModal from '@/components/ConfirmModal';

interface Lead {
  id: string;
  name: string;
  title: string;
  company: string;
  location: string;
  industry: string;
  company_size?: string | null;
  revenue?: string | null;
  website?: string | null;
  email?: string | null;
  email_revealed?: boolean;
  email_status?: string | null;
  phone?: string | null;
  phone_revealed?: boolean;
  headline?: string | null;
  photo_url?: string | null;
  linkedin_url?: string | null;
  source: string;
  added: string;
  updated_at?: string | null;
}

interface LeadSegment { id: string; name: string; lead_count: number; }
interface LeadNote { id: string; body: string; author: string; created_at: string; }

const tabList = ['Overview', 'Activity', 'Emails', 'Notes'];
// TODO: Unhide Activity, Emails, Notes tabs when features are ready
const visibleTabs = ['Overview'];

function formatNoteDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' at '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const searchContext = (location.state as any)?.searchContext;
  const returnTab = (location.state as any)?.returnTab;
  const [activeTab, setActiveTab] = useState('Overview');
  const [noteBody, setNoteBody] = useState('');
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState<string | null>(null);
  const [confirmDeleteLead, setConfirmDeleteLead] = useState(false);
  const queryClient = useQueryClient();
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => get<{ lead: Lead }>(API_URL.LEADS.GET(id!)),
    enabled: !!id,
  });

  const { data: segData } = useQuery({
    queryKey: ['lead-segments', id],
    queryFn: () => get<{ segments: LeadSegment[] }>(API_URL.LEADS.SEGMENTS(id!)),
    enabled: !!id,
  });

  const { data: notesData, isLoading: notesLoading } = useQuery({
    queryKey: ['lead-notes', id],
    queryFn: () => get<{ notes: LeadNote[] }>(API_URL.LEADS.NOTES(id!)),
    enabled: !!id,
  });

  const addNoteMutation = useMutation({
    mutationFn: (body: string) =>
      post(API_URL.LEADS.NOTES(id!), { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-notes', id] });
      setNoteBody('');
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => del(API_URL.LEADS.DELETE_NOTE(id!, noteId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lead-notes', id] }),
  });

  const deleteLeadMutation = useMutation({
    mutationFn: () => del(API_URL.LEADS.DELETE(id!)),
    onSuccess: () => {
      toast.success('Lead deleted successfully.');
      navigate('/app/leads', { state: { restoreContext: searchContext } });
    },
    onError: () => toast.error('Failed to delete lead.'),
  });

  const [revealingField, setRevealingField] = useState<'email' | 'phone' | null>(null);
  const [pendingRevealField, setPendingRevealField] = useState<'email' | 'phone' | null>(null);

  const revealMutation = useMutation({
    mutationFn: (field: 'email' | 'phone') =>
      post<{ field: string; value: string | null; revealed: boolean }>(
        API_URL.LEADS.REVEAL(id!),
        { field },
      ),
    onSuccess: (response, field) => {
      queryClient.invalidateQueries({ queryKey: ['credits-balance'] });
      queryClient.setQueryData(['lead', id], (old: any) => {
        if (!old?.data?.lead) return old;
        return {
          ...old,
          data: {
            ...old.data,
            lead: {
              ...old.data.lead,
              [field]: response.data.value ?? null,
              [`${field}_revealed`]: true,
            },
          },
        };
      });
      setRevealingField(null);
      if (!response.data.value) {
        toast.info(`No ${field} on file for this lead.`);
      }
    },
    onError: (error: any) => {
      setRevealingField(null);
      const code = error?.apiError?.code ?? error?.response?.data?.error?.code;
      if (code === 'INSUFFICIENT_CREDITS') {
        toast.error('No lead credits remaining. Upgrade your plan to reveal contact details.', { duration: 5000 });
      } else {
        toast.error('Failed to reveal field. Please try again.');
      }
    },
  });

  const handleReveal = (field: 'email' | 'phone') => {
    setPendingRevealField(field);
  };

  const confirmReveal = () => {
    if (!pendingRevealField) return;
    setRevealingField(pendingRevealField);
    revealMutation.mutate(pendingRevealField);
    setPendingRevealField(null);
  };

  const lead: Lead | null = data?.data?.lead ?? null;
  const leadSegments: LeadSegment[] = segData?.data?.segments ?? [];
  const notes: LeadNote[] = notesData?.data?.notes ?? [];

  // Register lead name in breadcrumb so TopBar shows name instead of ID
  useRegisterBreadcrumbTitle(id, lead?.name);

  // Record this lead in Recently Viewed once it loads (include search context from navigation state)
  const { record } = useRecentlyViewed();
  useEffect(() => {
    if (lead) {
      record({
        id: lead.id,
        name: lead.name,
        company: lead.company,
        title: lead.title,
        industry: lead.industry,
        location: lead.location,
        source: lead.source,
        added: lead.added,
        searchContext,
      });
    }
  }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="ldp-loading-state">
        <Loader2 size={22} className="ldp-spin" />
        <span className="ldp-loading-text">Loading lead...</span>
      </div>
    );
  }

  if (isError || !lead) {
    return (
      <div className="ldp-error-state">
        <AlertCircle size={32} color="var(--red, #ef4444)" />
        <p className="ldp-loading-text">Lead not found or could not be loaded.</p>
        <Link to="/app/leads" className="btn btn-secondary btn-sm">Back to Leads</Link>
      </div>
    );
  }

  const initials = lead.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="ldp-page">
      <Link to="/app/leads" state={{ restoreContext: searchContext, returnTab }} className="ldp-back">
        <ArrowLeft size={16} /> Back to Leads
      </Link>

      {/* Header card */}
      <div className="card ldp-header-card">
        <div className="ldp-header-inner">
          <div className="ldp-profile">
            {lead.photo_url ? (
              <img src={lead.photo_url} alt={lead.name} className="ldp-avatar ldp-avatar-img" />
            ) : (
              <div className="ldp-avatar ldp-avatar-initials">{initials}</div>
            )}
            <div>
              <h1 className="ldp-name">{lead.name}</h1>
              <p className="ldp-subtitle">{[lead.title, lead.company].filter(Boolean).join(' at ')}</p>
              {lead.headline && (
                <p className="ldp-headline">{lead.headline}</p>
              )}
              {lead.location && (
                <div className="ldp-meta-row">
                  <MapPin size={14} color="var(--gray-500)" />
                  <span className="ldp-meta-text">{lead.location}</span>
                </div>
              )}
              {/* Segments as tags */}
              {leadSegments.length > 0 && (
                <div className="ldp-badges">
                  {leadSegments.map(seg => (
                    <Link key={seg.id} to={`/app/segments/${seg.id}`} className="badge badge-blue ldp-seg-tag">
                      <Tag size={10} /> {seg.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="ldp-actions">
            {/* Send Email button — hidden temporarily */}
            {lead.source?.toLowerCase() === 'website' && (
              <button
                className="btn btn-secondary btn-sm ldp-delete-btn"
                onClick={() => setConfirmDeleteLead(true)}
                disabled={deleteLeadMutation.isPending}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="ldp-tabs">
        {tabList.filter(tab => visibleTabs.includes(tab)).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`ldp-tab ${activeTab === tab ? 'ldp-tab--active' : ''}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'Overview' && (
        <div className="ldp-overview-grid">
          {/* Left column — Company Insights */}
          <div className="card">
            <h3 className="ldp-card-title">Company Insights</h3>
            <div className="ldp-info-list">
              {[
                lead.company      && { icon: Building2,  label: 'Company',      value: lead.company,      href: lead.website || undefined },
                lead.industry     && { icon: Briefcase,  label: 'Industry',     value: lead.industry,     href: undefined },
                lead.company_size && { icon: Users,      label: 'Company Size', value: lead.company_size, href: undefined },
                lead.revenue      && { icon: DollarSign, label: 'Revenue',      value: lead.revenue,      href: undefined },
                lead.location     && { icon: MapPin,     label: 'Location',     value: lead.location,     href: undefined },
                lead.source       && { icon: Globe,      label: 'Source',       value: lead.source === 'website' ? 'Import - data' : lead.source, href: undefined },
              ].filter(Boolean).map((item: any) => (
                <div key={item.label} className="ldp-info-row">
                  <div className="ldp-info-icon"><item.icon size={16} color="var(--gray-500)" /></div>
                  <div>
                    <p className="ldp-info-label">{item.label}</p>
                    {item.href
                      ? <a href={item.href} target="_blank" rel="noreferrer" className="ldp-info-link">{item.value}</a>
                      : <p className="ldp-info-value">{item.value}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Middle column — Enrichment Details stacked above Contact Info */}
          <div className="ldp-right-stack">

            {/* Contact Information */}
            <div className="card">
              <h3 className="ldp-card-title">Contact Information</h3>
              <div className="ldp-info-list">

                {/* Email row */}
                <div className="ldp-info-row">
                  <div className="ldp-info-icon"><Mail size={16} color="var(--gray-500)" /></div>
                  <div className="ldp-reveal-field">
                    <p className="ldp-info-label">Email</p>
                    {lead.email_revealed && lead.email ? (
                      <a href={`mailto:${lead.email}`} className="ldp-info-link">{lead.email}</a>
                    ) : (
                      <div className="ldp-reveal-row">
                        <span className="ldp-masked-value" aria-hidden="true">
                          {lead.name.split(' ')[0]?.[0]?.toLowerCase() ?? 'u'}{'•••@••••.com'}
                        </span>
                        <button
                          className="ldp-reveal-btn"
                          onClick={() => handleReveal('email')}
                          disabled={revealingField === 'email'}
                        >
                          {revealingField === 'email'
                            ? <><Loader2 size={13} className="ldp-spin" /> Revealing...</>
                            : <><Eye size={13} /> Reveal · 1 credit</>}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Phone row */}
                <div className="ldp-info-row">
                  <div className="ldp-info-icon"><Phone size={16} color="var(--gray-500)" /></div>
                  <div className="ldp-reveal-field">
                    <p className="ldp-info-label">Phone</p>
                    {lead.phone_revealed && lead.phone ? (
                      <a href={`tel:${lead.phone}`} className="ldp-info-link">{lead.phone}</a>
                    ) : (
                      <div className="ldp-reveal-row">
                        <span className="ldp-masked-value" aria-hidden="true">+1 (•••) •••-••••</span>
                        <button
                          className="ldp-reveal-btn"
                          onClick={() => handleReveal('phone')}
                          disabled={revealingField === 'phone'}
                        >
                          {revealingField === 'phone'
                            ? <><Loader2 size={13} className="ldp-spin" /> Revealing...</>
                            : <><Eye size={13} /> Reveal · 1 credit</>}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              </div>

              <div className="ldp-kv-list ldp-contact-divider">
                <div className="ldp-kv-row"><span className="ldp-kv-label">Added</span><span className="ldp-kv-value">{lead.added}</span></div>
              </div>
            </div>

          </div>

          {/* Right column — Segments */}
          <div className="card">
            <h3 className="ldp-card-title">Segments</h3>
            {leadSegments.length === 0 ? (
              <p className="ldp-segment-empty">No segments yet.</p>
            ) : (
              <div className="ldp-seg-list">
                {leadSegments.map(seg => (
                  <Link key={seg.id} to={`/app/segments/${seg.id}`} className="ldp-seg-item">
                    <Tag size={14} color="var(--sky)" />
                    <span className="ldp-seg-name">{seg.name}</span>
                    <span className="ldp-seg-count">{seg.lead_count}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activity */}
      {activeTab === 'Activity' && (
        <div className="card">
          <div className="ldp-timeline">
            <div className="ldp-timeline-line" />
            {[
              { title: `Lead added via ${lead.source === 'website' ? 'Import - data' : lead.source}`, time: lead.added, icon: CheckCircle2 },
            ].map((a, i) => (
              <div key={i} className="ldp-tl-item">
                <div className="ldp-tl-dot"><a.icon size={14} color="var(--sky)" /></div>
                <p className="ldp-tl-title">{a.title}</p>
                <p className="ldp-tl-time"><Clock size={12} /> {a.time}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Emails */}
      {activeTab === 'Emails' && (
        <div className="card ldp-empty-state">
          <Mail size={32} color="var(--gray-300)" />
          <p>No emails sent to this lead yet.</p>
        </div>
      )}

      {/* Notes */}
      {activeTab === 'Notes' && (
        <div>
          {/* Compose */}
          <div className="card ldp-note-compose">
            <textarea
              ref={noteRef}
              className="ldp-note-textarea"
              placeholder="Add a note..."
              value={noteBody}
              onChange={e => setNoteBody(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && noteBody.trim()) {
                  e.preventDefault();
                  addNoteMutation.mutate(noteBody.trim());
                }
              }}
            />
            <div className="ldp-note-compose-footer">
              {addNoteMutation.isError && (
                <span className="ldp-note-error">Failed to save note. Try again.</span>
              )}
              <button
                className="btn btn-primary btn-sm"
                disabled={!noteBody.trim() || addNoteMutation.isPending}
                onClick={() => noteBody.trim() && addNoteMutation.mutate(noteBody.trim())}
              >
                {addNoteMutation.isPending ? <Loader2 size={14} className="ldp-spin" /> : null}
                Add Note
              </button>
            </div>
          </div>

          {/* Notes list */}
          {notesLoading ? (
            <div className="ldp-note-loading">
              <Loader2 size={18} className="ldp-spin" color="var(--gray-400)" />
            </div>
          ) : notes.length === 0 ? (
            <div className="card ldp-empty-state">
              <MessageSquare size={32} color="var(--gray-300)" />
              <p>No notes yet.</p>
            </div>
          ) : (
            <div className="ldp-notes-list">
              {notes.map(note => (
                <div key={note.id} className="card ldp-note-card">
                  <div className="ldp-note-header">
                    <span className="ldp-note-author">{note.author || 'Unknown'}</span>
                    <div className="ldp-note-header-right">
                      <span className="ldp-note-time">{formatNoteDate(note.created_at)}</span>
                      <button
                        className="ldp-note-delete"
                        onClick={() => setConfirmDeleteNoteId(note.id)}
                        aria-label="Delete note"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="ldp-note-body">{note.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {pendingRevealField && (
        <ConfirmModal
          title="Reveal Contact Info"
          message={`1 lead credit will be deducted from your account to reveal this lead's ${pendingRevealField}. Continue?`}
          confirmLabel="Reveal · 1 credit"
          danger={false}
          onConfirm={confirmReveal}
          onCancel={() => setPendingRevealField(null)}
        />
      )}

      {confirmDeleteNoteId && (
        <ConfirmModal
          title="Delete Note"
          message="Are you sure you want to delete this note? This action cannot be undone."
          confirmLabel="Delete"
          onConfirm={() => {
            deleteNoteMutation.mutate(confirmDeleteNoteId);
            setConfirmDeleteNoteId(null);
          }}
          onCancel={() => setConfirmDeleteNoteId(null)}
        />
      )}

      {confirmDeleteLead && (
        <ConfirmModal
          title="Delete Lead"
          message={`Are you sure you want to delete ${lead.name}? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => {
            setConfirmDeleteLead(false);
            deleteLeadMutation.mutate();
          }}
          onCancel={() => setConfirmDeleteLead(false)}
        />
      )}
    </div>
  );
}
